<template>
  <v-container fluid>
    <v-card :loading="false">
      <v-overlay :absolute="true" :value="isComponentLoading">
        <v-progress-circular indeterminate size="24"></v-progress-circular>
      </v-overlay>
      <v-row class="px-3 pt-8">
        <v-col cols="12" md="3">
          <v-text-field
            v-model="searchText"
            :hint="$t('global.filter.hints.search_name')"
            persistent-hint
            single-line
            :loading="false"
          >
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="searchParams.active"
            :hint="$t('global.filter.hints.status')"
            :items="statusItems"
            persistent-hint
            single-line
            :loading="false"
          ></v-select>
        </v-col>
        <v-col v-if="rolesItems" cols="12" sm="6" md="2">
          <v-select
            v-model="searchParams.role"
            :hint="$t('global.filter.hints.role')"
            :items="rolesItems"
            persistent-hint
            single-line
            :loading="false"
          ></v-select>
        </v-col>
        <v-col v-if="cleanerRegionsItems" cols="12" sm="6" md="2">
          <v-select
            v-model="searchParams.region"
            :hint="$t('global.filter.hints.region')"
            :items="cleanerRegionsItems"
            persistent-hint
            single-line
            :loading="false"
          ></v-select>
        </v-col>
        <v-col
          v-if="cleanerRegionsItems"
          class="d-flex align-center"
          cols="12"
          sm="6"
          md="3"
        >
          <v-select
            v-model="searchParams.order_by"
            :hint="$t('global.filter.hints.sort_by')"
            :items="orderByItems"
            persistent-hint
            single-line
            :loading="false"
          ></v-select>
          <order-to
            v-model="searchParams.order_to"
            :small="false"
            class="ml-2"
          ></order-to>
        </v-col>
      </v-row>
      <download-file-button
        v-if="account.role === MANAGER_TOP"
        :use-download-report-users="true"
        style="position: absolute; top: 5px; right: 5px"
        @click="$emit('download-excel')"
      />
      <div style="position: absolute; top: 0; left: 0">
        <v-btn
          v-if="$router.currentRoute.name === 'cleaners.index'"
          tile
          color="success"
          to="/cleaners-schedule"
        >
          <v-icon left>
            mdi-table
          </v-icon>
          {{ $t('cleaners.schedule') }}
        </v-btn>
        <v-btn
          v-if="$router.currentRoute.name === 'cleaners.index'"
          tile
          color="primary"
          to="/cleaners-checklists"
          class="ml-4"
        >
          <v-icon left>
            mdi-table
          </v-icon>
          {{ $t('cleaners.checklists') }}
        </v-btn>
      </div>
    </v-card>
  </v-container>
</template>

<script>
/* constants */
import { CLEANERS, MANAGER_TOP, MANAGERS } from '@/constants/roles';
/* mixins */
import isComponentLoading from '@/mixins/isComponentLoading';
import regionsFilter from '@/mixins/filters/regionsFilter';
import regionsCleanersFilter from '@/mixins/filters/regionsCleanersFilter';

import statusFilter from '@/mixins/filters/statusFilter';
/* components */
import DownloadFileButton from '@/components/minialerts/DownloadFileButton.vue';
import OrderTo from '@/components/minialerts/OrderTo.vue';
import i18n from '@/i18n';

export default {
  name: 'FilterCleaners',
  mixins: [isComponentLoading, regionsFilter, regionsCleanersFilter, statusFilter],
  components: {
    DownloadFileButton,
    OrderTo,
  },
  props: {
    defaultSearchParams: {
      type: Object,
      default: () => ({
        search: '',
        active: 1,
        role: null,
        region: null,
      }),
    },
  },
  data() {
    return {
      MANAGER_TOP,
      searchText: '',
      searchTextTimeout: null,
      searchParams: {},
    };
  },
  mounted() {
    this.searchParams = {
      search: this.defaultSearchParams.search,
      active: this.defaultSearchParams.active,
      role: this.defaultSearchParams.role,
      region: this.defaultSearchParams.region,
      order_by: this.defaultSearchParams.order_by || 'carier_start_at',
      order_to: this.defaultSearchParams.order_to || 'asc',
    };
  },
  computed: {
    orderByItems() {
      return [
        {
          text: i18n.t('cleaners.sort_by.name'),
          value: 'name',
        },
        {
          text: i18n.t('cleaners.sort_by.carier_start_at'),
          value: 'carier_start_at',
        },
        {
          text: i18n.t('cleaners.sort_by.created_at'),
          value: 'created_at',
        },
        {
          text: i18n.t('cleaners.sort_by.average_rating'),
          value: 'average_rating',
        },
      ];
    },
    rolesItems() {
      switch (this.account.role) {
        case MANAGERS.MANAGER_TOP:
          return [
            {
              text: i18n.t('enums.roles.all'),
              value: null,
            },
            {
              text: i18n.t('enums.roles.foreman'),
              value: CLEANERS.FOREMAN,
            },
            {
              text: i18n.t('enums.roles.cleaner_base'),
              value: CLEANERS.CLEANER_BASE,
            },
            {
              text: i18n.t('enums.roles.cleaner_complex'),
              value: CLEANERS.CLEANER_COMPLEX,
            },
          ];
        case MANAGERS.MANAGER_COMPLEX:
          return [
            {
              text: i18n.t('enums.roles.all'),
              value: null,
            },
            {
              text: i18n.t('enums.roles.foreman'),
              value: CLEANERS.FOREMAN,
            },
            {
              text: i18n.t('enums.roles.cleaner_complex'),
              value: CLEANERS.CLEANER_COMPLEX,
            },
          ];
        default:
          return null;
      }
    },
    account() {
      return this.$store.getters['account/account'];
    },
  },
  watch: {
    searchParams: {
      handler(newVal) {
        this.$emit('filtering', newVal);
      },
      deep: true,
    },
    searchText(newVal) {
      clearTimeout(this.searchTextTimeout);
      this.searchTextTimeout = setTimeout(() => {
        this.searchParams.search = newVal;
      }, 500);
    },
  },
};
</script>

<style scoped></style>
